import PropTypes from 'prop-types';
import React, { Component } from 'react';
import get from 'lodash/get';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import PromotionalMessage from '../SpecialOffers/PromotionalMessage';
import OOSMessage from '../OOSMessage/OOSMessage';
import * as styles from './PricingInfo.css';

const defaults = {
    promotions: [],
};

class PricingInfo extends Component {
    static defaultProps = {
        pricingDetails: {},
        priceInBag: false,
        isSticky: false,
        fromMiniPDP: false,
        isProductInStock: true,
        couponPriceDetails: [],
        featureFlags: {},
        deviceType: {},
        shouldShortenPrice: false,
    };

    static propTypes = {
        pricingDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        productType: PropTypes.string.isRequired,
        priceInBag: PropTypes.bool,
        isSticky: PropTypes.bool,
        fromMiniPDP: PropTypes.bool,
        isProductInStock: PropTypes.bool,
        couponPriceDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        featureFlags: PropTypes.object,
        deviceType: PropTypes.object,
        shouldShortenPrice: PropTypes.bool,
    };

    static bottomBorderClass = ['border-b', 'border-solid', 'border-gray-15'];

    static fontBoldBlack = ['font-bold', 'leading-7', 'pr-0.5', 'text-black', 'text-xLg'];

    priceRenderer = () => {
        let priceComponent;
        const { priceInBag, pricingDetails } = this.props;
        const price =
            !pricingDetails.mapPrice && (pricingDetails.defaults || pricingDetails.original);
        /* istanbul ignore next */
        if (priceInBag || price === '$0') {
            priceComponent = (
                <span data-automation-id="view-price-in-bag" className={styles.price}>
                    View Price in Cart
                </span>
            );
        } else if (price) {
            priceComponent = (
                <span
                    data-automation-id="at-price-value"
                    className={dt([...PricingInfo.fontBoldBlack])}
                >
                    {price}
                </span>
            );
        }
        return priceComponent;
    };

    wasPrice() {
        const { pricingDetails, isSticky, couponPriceDetails, shouldShortenPrice } = this.props;

        const wasPriceElement =
            pricingDetails.original && pricingDetails.original !== '$0' ? (
                <p className={dt(['italic', 'leading-5', 'text-sm', 'text-gray-60'])}>
                    {pricingDetails.percentOff && (
                        <span data-automation-id="price-percent-off" className={dt(['px-2'])}>
                            {pricingDetails.percentOff}
                        </span>
                    )}
                    {pricingDetails.original && (
                        <span
                            data-automation-id="price-original-price"
                            className={dt(['pl-2', 'line-through'])}
                        >
                            {pricingDetails.original}
                        </span>
                    )}
                </p>
            ) : (
                ''
            );

        /* istanbul ignore next */
        if (isSticky) {
            return !(couponPriceDetails && couponPriceDetails.length) && !shouldShortenPrice
                ? wasPriceElement
                : '';
        }
        return wasPriceElement;
    }

    renderMapPrice = (pricingDetails) => {
        const mapStyles = this?.props?.isSticky ? [''] : ['py-2', ...PricingInfo.bottomBorderClass];
        return (
            <p
                className={dt([...mapStyles, ...PricingInfo.fontBoldBlack])}
                data-automation-id="at-price-map-price-label"
            >
                {pricingDetails.mapPrice}
            </p>
        );
    };

    renderPrices = () => {
        const {
            isSticky,
            pricingDetails,
            couponExclusionMessage,
            isProductInStock,
            fromMiniPDP,
            featureFlags: { bxgxPromotions = true },
        } = this.props;
        const outOfStockErrorStyle = !isProductInStock && !isSticky ? '!pt-1' : '';
        const promotions = get(pricingDetails, 'promotions', defaults.promotions);
        const isPromotionalLabel = promotions.length && !pricingDetails.mapPrice;

        const marketingLabelSpacing = pricingDetails.marketingLabel ? 'pb-2' : dt(['pb-2', 'pt-4']);
        const smMdContainerStyling = ['smMd:flex-col'];
        const borderStyle =
            couponExclusionMessage || isSticky ? [] : [...PricingInfo.bottomBorderClass];
        const commonPricingContainerStyling = ['flex', 'font-open-sans', 'items-baseline'];
        const regularPDPPricesContainerStyling = [
            marketingLabelSpacing,
            ...borderStyle,
            ...smMdContainerStyling,
            outOfStockErrorStyle,
        ];
        const stickyHeaderSectionStyling = [];

        /* istanbul ignore next */
        return (
            <section className={isSticky ? dt([...stickyHeaderSectionStyling]) : dt([''])}>
                <div
                    className={[
                        dt([...commonPricingContainerStyling]),
                        dt(!isSticky ? [...regularPDPPricesContainerStyling] : ['']),
                    ].join(' ')}
                >
                    <div
                        className={dt(['flex', 'items-baseline'])}
                        data-automation-id={`${pricingDetails.type}-price-info`}
                    >
                        {this.priceRenderer()}
                        {pricingDetails.type && (
                            <span
                                data-automation-id="at-price-label"
                                className={dt([
                                    'font-semibold',
                                    'italic',
                                    'pl-0.5',
                                    'pr-2',
                                    'text-brand-red',
                                    'text-sm',
                                    'leading-5',
                                ])}
                            >
                                {pricingDetails.type}
                            </span>
                        )}
                        {this.wasPrice()}
                    </div>
                    {bxgxPromotions &&
                    !isSticky &&
                    isPromotionalLabel &&
                    !pricingDetails.hasBopisPrice ? (
                        <div className={dt(['lg:ml-auto'])}>
                            <PromotionalMessage promotions={promotions} />
                        </div>
                    ) : null}
                </div>
            </section>
        );
    };

    render() {
        const {
            pricingDetails,
            isSticky,
            isProductInStock,
            fromMiniPDP,
            featureFlags: { enableMarketingLabel = true },
        } = this.props;
        const price =
            !pricingDetails.mapPrice && (pricingDetails.defaults || pricingDetails.original);
        const formattedPrice = price && price.substring(1);
        const stickyPricingStyling = [''];

        return (
            <div className={isSticky ? dt([...stickyPricingStyling]) : dt([''])}>
                {enableMarketingLabel &&
                isProductInStock &&
                pricingDetails &&
                pricingDetails.marketingLabel ? (
                    <p
                        className={dt([
                            'font-semibold',
                            'italic',
                            'text-sm',
                            'leading-5',
                            'text-brand-red',
                            isSticky ? '' : 'pt-2',
                        ])}
                        data-automation-id="at-price-promotions-label"
                    >
                        {pricingDetails.marketingLabel}
                    </p>
                ) : null}
                {!isProductInStock && !isSticky ? <OOSMessage fromMiniPDP={fromMiniPDP} /> : null}
                {pricingDetails && pricingDetails.mapPrice
                    ? this.renderMapPrice(pricingDetails)
                    : this.renderPrices()}
                {this.props.productType && this.props.productType === 'silver' ? (
                    <p className={styles.guranteeMatch}>
                        <span className={styles.priceMatch}>Price Match</span> Guarantee
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.link}
                            href="/jsp/customerservice/serviceContent.jsp?pageId=pg40014800010"
                        >
                            See Details
                        </a>
                    </p>
                ) : (
                    ''
                )}
            </div>
        );
    }
}

export default PricingInfo;
